import { CHROME_EXTENSION_URL } from '../../lib/constants';
import { capture } from '@/lib/analytics';

interface ExtensionInstallToastProps {
  /** The site the user chose in onboarding — names the exact payoff in the copy. */
  url?: string;
  extensionDetected?: boolean;
  onOpenSite?: () => void;
  error?: string | null;
}

/** Friendly host label (with port, e.g. "localhost:3000"), www stripped. */
function hostLabel(url: string): string | null {
  try {
    return new URL(url).host.replace(/^www\./, '') || null;
  } catch {
    return null;
  }
}

export function ExtensionInstallToast({ url, extensionDetected = false, onOpenSite, error }: ExtensionInstallToastProps = {}) {
  const label = url ? hostLabel(url) : null;
  // Outcome-framed when we know the site: the prompt now names the exact reward
  // (their site opening) at the moment they hit the install gate, instead of a
  // generic "finish setting up".
  const message = extensionDetected ? `Open ${label ?? 'your site'} with the OverlayQA sidebar to start capturing.` : label
    ? `Install the OverlayQA Chrome extension to open ${label} with the sidebar.`
    : 'To finish setting up, install the OverlayQA Chrome extension.';
  return (
    <section
      className="rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 flex items-center gap-3"
      role="alert"
      aria-label={extensionDetected ? 'Open your site' : 'Extension required'}
    >
      <svg
        className="w-5 h-5 text-primary-600 flex-shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
        />
      </svg>
      <p className="text-sm text-primary-800 flex-1 min-w-0 break-words">
        {error ?? message}
      </p>
      <button
        type="button"
        className="btn-primary btn-sm shrink-0"
        onClick={() => {
          if (extensionDetected) { onOpenSite?.(); return; }
          capture('dashboard_extension_install_click', { source: 'install_toast' });
          window.open(CHROME_EXTENSION_URL, '_blank', 'noopener');
        }}
      >
        <span className="text-cap-trim">{extensionDetected ? error ? 'Try again' : 'Open your site' : 'Install extension'}</span>
      </button>
    </section>
  );
}
